/**
 * Care Admin AI Agent — Groq-powered tool-calling assistant
 *
 * WARNING: PROTOTYPE ONLY — The Groq API key is exposed client-side via
 * REACT_APP_GROQ_API_KEY. For production, move the entire agent loop
 * (system prompt, tool definitions, tool execution, and Groq calls) to a
 * secure Firebase Function or backend endpoint and call it from the frontend
 * through the existing authenticated API pattern in src/api.js.
 *
 * Architecture:
 *   1. User message + conversation history sent to Groq with tool definitions.
 *   2. If the model returns tool_calls, we execute them against real API functions.
 *   3. Results are appended as tool messages and sent back to Groq.
 *   4. Loop repeats (max MAX_TOOL_ITERATIONS) until the model produces a final text answer.
 */

import Groq from 'groq-sdk';
import logger from '../logger';
import {
  getAdminDashboardMetrics,
  getActiveUsersMetric,
  getLearnersPaginated,
  getLearnersById,
  getCourses,
  getCourseEnrollments,
  getActivityLogs,
  getAdminsPaginated,
  getCertificates,
  fetchQuizzes,
} from '../api';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;
const MODEL = 'openai/gpt-oss-120b';
const MAX_TOOL_ITERATIONS = 4; // Safety cap to avoid runaway loops

// ---------------------------------------------------------------------------
// System prompt — instructs the model how to behave
// ---------------------------------------------------------------------------

const SYSTEM_PROMPT = `You are "EnRiCH DLP Assistant", an AI agent embedded in the EnRiCH DLP Admin Dashboard. You help administrators understand their platform data by querying real databases through the tools available to you.

CAPABILITIES:
- Query users (learners and admins), courses, enrollments, activity logs, certificates, quizzes, and dashboard metrics.
- Combine data from multiple tools to answer complex questions.
- Perform calculations, comparisons, and trend analysis on the data you retrieve.

RULES:
1. Think step-by-step. Decide which tool(s) to call before acting.
2. ONLY use the tools provided — never fabricate data or invent numbers.
3. If a question cannot be answered with available tools, say so honestly.
4. When presenting lists or comparisons, use **markdown tables**.
5. Bold key metrics and numbers for quick scanning.
6. Keep responses concise and admin-friendly — no technical jargon about the tools themselves.
7. If a query is ambiguous, ask a clarifying question instead of guessing.
8. When returning large result sets, summarize the top items and offer to show more.
9. Always indicate the timeframe or scope of data when relevant (e.g., "as of now", "in the last 30 days").
10. If a tool call fails, explain the issue to the admin in plain language and try an alternative approach if possible.`;

// ---------------------------------------------------------------------------
// Tool definitions — each maps to a real API function in src/api.js
// ---------------------------------------------------------------------------

const TOOL_DEFINITIONS = [
  {
    type: 'function',
    function: {
      name: 'get_dashboard_metrics',
      description:
        'Retrieve high-level dashboard metrics for the entire platform. Returns total courses, total enrollments, completed courses, daily active users (DAU), monthly active users (MAU), average quiz score, course status breakdown, and top-performing staff. Use this tool when the admin asks for an overview, summary, or general statistics about the platform.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_active_users_metric',
      description:
        'Get the count and percentage of active vs inactive users over the last 30 days. Returns active count, inactive count, total users, and percent active. Use this when the admin asks about user activity levels, active user counts, or engagement rates.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_learners',
      description:
        'Search and list learners (health workers) with optional search query and pagination. Use this to find specific users by name/email, or to browse all learners. Returns a paginated list with learner details including name, email, status, and enrollment info.',
      parameters: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Optional search query to filter learners by name or email. Leave empty to list all learners.',
          },
          page: {
            type: 'number',
            description: 'Page number for pagination (default: 1)',
          },
          limit: {
            type: 'number',
            description: 'Number of learners per page (default: 25, max recommended: 50)',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_learner_details',
      description:
        'Get detailed profile for a specific learner by their ID. Returns full profile including name, email, phone, gender, job title, facility, enrollment status, course progress, completed courses, pending courses, quiz scores, and test performance. Use this when the admin asks about a specific user\'s progress or details.',
      parameters: {
        type: 'object',
        properties: {
          learner_id: {
            type: 'string',
            description: 'The unique ID of the learner to retrieve details for',
          },
        },
        required: ['learner_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_courses',
      description:
        'List all courses with optional filtering by status and pagination. Each course includes: title, description, category, learner count, module count, completion count, pending count, test count, question count, average test score, publication status, and timestamps. Use this to answer questions about course listings, course performance, enrollment numbers, or completion rates.',
      parameters: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            description: 'Filter by course status. Options: "all" (default), "published", "draft", "archived"',
            enum: ['all', 'published', 'draft', 'archived'],
          },
          page: {
            type: 'number',
            description: 'Page number for pagination (default: 1)',
          },
          limit: {
            type: 'number',
            description: 'Number of courses per page (default: 20, use higher values like 50-100 to get more courses at once)',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_course_enrollments',
      description:
        'Get all enrollments for a specific course by course ID. Returns a list of enrolled users with their enrollment status, progress, and completion details. Use this when the admin asks about who is enrolled in a specific course, enrollment numbers for a course, or completion status of users in a course.',
      parameters: {
        type: 'object',
        properties: {
          course_id: {
            type: 'string',
            description: 'The unique ID of the course to get enrollments for',
          },
        },
        required: ['course_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_activity_logs',
      description:
        'Retrieve system activity logs showing recent actions performed on the platform (e.g., logins, course completions, profile updates, enrollments). Supports pagination and optional filtering by the user (causer) who performed the action. Use this to answer questions about recent activity, audit trails, or what specific users have been doing.',
      parameters: {
        type: 'object',
        properties: {
          page: {
            type: 'number',
            description: 'Page number for pagination (default: 1)',
          },
          per_page: {
            type: 'number',
            description: 'Number of log entries per page (default: 15, max recommended: 50)',
          },
          causer_id: {
            type: 'string',
            description: 'Optional user ID to filter logs by a specific user who performed the actions',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_admins',
      description:
        'Search and list admin users with optional search query and pagination. Returns admin details including name, email, role (Admin or Super Admin), and status. Use this when the admin asks about admin accounts, admin management, or wants to find a specific admin.',
      parameters: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Optional search query to filter admins by name or email',
          },
          page: {
            type: 'number',
            description: 'Page number for pagination (default: 1)',
          },
          limit: {
            type: 'number',
            description: 'Number of admins per page (default: 25)',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_certificates',
      description:
        'List certificates with optional filtering by approval status and pagination. Returns certificate details including learner info, course, status (pending/approved/rejected), and issue date. Use this when the admin asks about certificate requests, approvals, pending reviews, or certificate statistics.',
      parameters: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            description: 'Filter by certificate status. Options: "all" (default), "pending", "approved", "rejected"',
          },
          page: {
            type: 'number',
            description: 'Page number for pagination (default: 1)',
          },
          limit: {
            type: 'number',
            description: 'Number of certificates per page (default: 25)',
          },
        },
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_quizzes',
      description:
        'Get all quizzes/tests across the platform. Returns quiz details including title, description, category, number of completions, pending attempts, question count, and average score percentile. Use this when the admin asks about quiz performance, test results, question banks, or assessment statistics.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
];

// ---------------------------------------------------------------------------
// Tool execution — maps tool names to actual API calls
// ---------------------------------------------------------------------------

/**
 * Execute a single tool call and return a JSON-serializable result.
 * Errors are caught and returned as structured error objects so the
 * LLM can explain the issue to the user instead of crashing.
 */
async function executeTool(name, args) {
  try {
    switch (name) {
      case 'get_dashboard_metrics': {
        const data = await getAdminDashboardMetrics();
        if (!data) return { error: 'Dashboard metrics are currently unavailable.' };
        return data;
      }

      case 'get_active_users_metric': {
        const data = await getActiveUsersMetric();
        if (!data) return { error: 'Active users metric is currently unavailable.' };
        return data;
      }

      case 'search_learners': {
        const { search = '', page = 1, limit = 25 } = args || {};
        const data = await getLearnersPaginated(page, limit, search);
        return data || { data: [], meta: { total: 0 } };
      }

      case 'get_learner_details': {
        const { learner_id } = args || {};
        if (!learner_id) return { error: 'learner_id is required.' };
        const data = await getLearnersById(learner_id);
        if (!data) return { error: `Learner with ID "${learner_id}" not found.` };
        return data;
      }

      case 'get_courses': {
        const { status = 'all', page = 1, limit = 20 } = args || {};
        const data = await getCourses({ page, limit, status });
        return { courses: data || [], count: (data || []).length };
      }

      case 'get_course_enrollments': {
        const { course_id } = args || {};
        if (!course_id) return { error: 'course_id is required.' };
        const data = await getCourseEnrollments(course_id);
        return { enrollments: data || [], count: (data || []).length };
      }

      case 'get_activity_logs': {
        const { page = 1, per_page = 15, causer_id = null } = args || {};
        const data = await getActivityLogs(page, per_page, causer_id);
        return data || { data: [], total: 0 };
      }

      case 'search_admins': {
        const { search = '', page = 1, limit = 25 } = args || {};
        const data = await getAdminsPaginated(page, limit, search);
        return data || { data: [], meta: { total: 0 } };
      }

      case 'get_certificates': {
        const { status = 'all', page = 1, limit = 25 } = args || {};
        const data = await getCertificates(status, page, limit);
        return data || { data: [], meta: { total: 0 } };
      }

      case 'get_quizzes': {
        const data = await fetchQuizzes();
        return data || { data: [] };
      }

      default:
        return { error: `Unknown tool: ${name}` };
    }
  } catch (err) {
    logger.error(`Tool "${name}" execution failed:`, err);
    const message = err?.response?.data?.message || err?.message || 'Unknown error';
    return { error: `Tool "${name}" failed: ${message}` };
  }
}

// ---------------------------------------------------------------------------
// Truncation helper — prevent oversized tool results from blowing up context
// ---------------------------------------------------------------------------

function truncateResult(result, maxChars = 12000) {
  const json = JSON.stringify(result);
  if (json.length <= maxChars) return json;

  // If the result is an array-like structure, try to keep summary + truncated items
  if (Array.isArray(result)) {
    const truncated = result.slice(0, 20);
    return JSON.stringify({
      _truncated: true,
      _totalItems: result.length,
      _showing: truncated.length,
      items: truncated,
    });
  }

  // If result has a data array, truncate that
  if (result?.data && Array.isArray(result.data)) {
    const truncated = { ...result, data: result.data.slice(0, 20) };
    truncated._truncated = true;
    truncated._totalItems = result.data.length;
    truncated._showing = truncated.data.length;
    return JSON.stringify(truncated);
  }

  // If result has a courses array, truncate that
  if (result?.courses && Array.isArray(result.courses)) {
    const truncated = { ...result, courses: result.courses.slice(0, 20) };
    truncated._truncated = true;
    truncated._totalItems = result.courses.length;
    truncated._showing = truncated.courses.length;
    return JSON.stringify(truncated);
  }

  // If result has an enrollments array, truncate that
  if (result?.enrollments && Array.isArray(result.enrollments)) {
    const truncated = { ...result, enrollments: result.enrollments.slice(0, 20) };
    truncated._truncated = true;
    truncated._totalItems = result.enrollments.length;
    truncated._showing = truncated.enrollments.length;
    return JSON.stringify(truncated);
  }

  // Last resort: hard truncate the JSON string
  return json.slice(0, maxChars) + '... [TRUNCATED]';
}

// ---------------------------------------------------------------------------
// Main agent loop
// ---------------------------------------------------------------------------

/**
 * Run the Care Admin AI Agent.
 *
 * @param {string} userMessage - The latest user message.
 * @param {Array<{role: string, content: string}>} conversationHistory - Prior messages.
 * @param {AbortSignal} [signal] - Optional abort signal for cancellation.
 * @returns {Promise<{message: string}>} The assistant's final text response.
 */
export async function runAgentQuery(userMessage, conversationHistory = [], signal = null) {
  // --- Guard: check for API key ---
  if (!GROQ_API_KEY) {
    return {
      message:
        '**Configuration Required**\n\nThe Groq API key is not configured. Please add `REACT_APP_GROQ_API_KEY` to your `.env` file and restart the development server.\n\n' +
        'To get a free API key, visit [console.groq.com](https://console.groq.com).',
    };
  }

  // --- Initialize Groq client ---
  const groq = new Groq({
    apiKey: GROQ_API_KEY,
    dangerouslyAllowBrowser: true, // Required for client-side use — see production warning above
  });

  // --- Build message history for the model ---
  const messages = [
    { role: 'system', content: SYSTEM_PROMPT },
    // Include prior conversation turns (only role + content, capped for context)
    ...conversationHistory.slice(-20).map(m => ({
      role: m.role,
      content: m.content,
    })),
  ];

  // Avoid duplicating the latest user message if it's already the last item in history
  const lastHistoryMsg = conversationHistory[conversationHistory.length - 1];
  if (!lastHistoryMsg || lastHistoryMsg.content !== userMessage || lastHistoryMsg.role !== 'user') {
    messages.push({ role: 'user', content: userMessage });
  }

  // --- Agent loop: call model → execute tools → repeat ---
  for (let iteration = 0; iteration < MAX_TOOL_ITERATIONS; iteration++) {
    // Check for cancellation
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const response = await groq.chat.completions.create({
      model: MODEL,
      messages,
      tools: TOOL_DEFINITIONS,
      tool_choice: 'auto',
      temperature: 0.1, // Low temperature for factual, consistent answers
      max_tokens: 4096,
    });

    const choice = response.choices[0];

    // If model finished without tool calls, return the text response
    if (choice.finish_reason === 'stop' || !choice.message.tool_calls?.length) {
      return {
        message: choice.message.content || 'I wasn\'t able to generate a response. Please try rephrasing your question.',
      };
    }

    // Model wants to call tools — append its message to history
    messages.push(choice.message);

    // Execute all tool calls in parallel
    const toolCalls = choice.message.tool_calls;
    logger.debug(`Agent iteration ${iteration + 1}: executing ${toolCalls.length} tool(s):`, toolCalls.map(tc => tc.function.name));

    const toolResults = await Promise.all(
      toolCalls.map(async (toolCall) => {
        let args = {};
        try {
          args = JSON.parse(toolCall.function.arguments || '{}');
        } catch {
          args = {};
        }

        const result = await executeTool(toolCall.function.name, args);

        return {
          role: 'tool',
          tool_call_id: toolCall.id,
          content: truncateResult(result),
        };
      })
    );

    // Append all tool results to messages
    messages.push(...toolResults);
  }

  // If we exhausted all iterations without a final answer, ask the model to wrap up
  messages.push({
    role: 'user',
    content: 'Please provide your final answer based on the data you have gathered so far. Do not call any more tools.',
  });

  const finalResponse = await groq.chat.completions.create({
    model: MODEL,
    messages,
    temperature: 0.1,
    max_tokens: 4096,
  });

  return {
    message: finalResponse.choices[0]?.message?.content || 'I gathered some data but could not formulate a response. Please try a more specific question.',
  };
}
